'use client'

import { Box, Stack } from '@mui/material'
import type { UIMessage } from 'ai'

import CollapsiblePrompt from './CollapsiblePrompt'
import ContextRefChips from './ContextRefChips'
import FeedbackControls from './FeedbackControls'
import AttachmentChip from './attachments/AttachmentChip'
import { readContextRefs } from './contextRefs'
import { readAttachment, readFeedbackRating } from './stream/dataParts'
import AssistantTurn from './turn/AssistantTurn'

type MessageItemProps = {
  message: UIMessage
  conversationId: string
  /** A shared conversation the viewer may read but not act on. */
  readOnly: boolean
  /** True for the newest message while its reply is still streaming. */
  isStreaming: boolean
  canAnswer: boolean
  onAnswer: (answer: string) => void
  /** Called when an action this message proposed reaches its outcome. */
  onActionSettled: () => void
}

export default function MessageItem({
  message,
  conversationId,
  readOnly,
  isStreaming,
  canAnswer,
  onAnswer,
  onActionSettled
}: MessageItemProps) {
  if (message.role === 'user') return <UserMessage message={message} />
  if (message.role !== 'assistant') return null

  return (
    <Stack spacing={1} data-testid="assistant-message" sx={{ alignSelf: 'stretch' }}>
      <AssistantTurn
        parts={message.parts}
        readOnly={readOnly}
        canAnswer={canAnswer}
        onAnswer={onAnswer}
        onActionSettled={onActionSettled}
        isStreaming={isStreaming}
      />
      {!isStreaming && !readOnly && (
        <FeedbackControls
          conversationId={conversationId}
          messageId={message.id}
          initialRating={readFeedbackRating(message)}
        />
      )}
    </Stack>
  )
}

function UserMessage({ message }: { message: UIMessage }) {
  const text = message.parts.flatMap((part) => (part.type === 'text' ? [part.text] : [])).join('\n')
  const attachments = message.parts.flatMap((part) => {
    const attachment = readAttachment(part)
    return attachment ? [attachment] : []
  })
  return (
    <Stack
      spacing={0.5}
      data-testid="user-message"
      sx={{ alignSelf: 'stretch', alignItems: 'stretch' }}
    >
      <ContextRefChips contextRefs={readContextRefs(message)} align="flex-start" data-testid="user-message-context" />
      {attachments.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-start' }}>
          {attachments.map((attachment) => (
            <AttachmentChip key={attachment.attachmentId} filename={attachment.filename} />
          ))}
        </Box>
      )}
      {text && <CollapsiblePrompt text={text} />}
    </Stack>
  )
}
